import { Suspense, lazy, useEffect } from 'react';
import { INVOICE_MAX_AGE_MS } from './pos/invoiceQueue';
import { purgeStaleInvoices } from './pos/sessions';
import { Outlet, Route, Routes } from 'react-router';
import { useNativeLinks } from './nativeLinks';
import { Layout, RequireAuth } from './components/Layout';
import { EventShell } from './components/EventNav';
import { ComposeSheet } from './components/ComposeSheet';
import {
  ForgotPasswordPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from './pages/auth';
import { RootPage } from './pages/landing';
import { DevicePosPage, PosPage } from './pages/pos';
import { QuickRegisterPage } from './pages/posStart';
import { KeyedPairingPage, PairScanPage } from './pages/nativeStart';
import { InboxPage, OrgMessagePage, ThreadPage } from './pages/inbox';
import { TeamInboxPage, TeamThreadPage } from './pages/teamInbox';

// Welle 5: Alle Seiten außer Start, Anmelden/Registrieren und Kasse laden ihren Code erst beim Öffnen. Die Kasse
// bleibt im Hauptpaket, damit sie ohne Netz sofort startet. Lazy-Pakete landen im Vorab-Cache des Service Workers
// (apps/web/vite/precache.ts nimmt alle Build-Dateien auf), die Kasse funktioniert also auch dann offline, wenn
// eine Seite zuvor nicht geöffnet wurde.
const AcceptInvitationPage = lazy(() =>
  import('./pages/account').then((m) => ({ default: m.AcceptInvitationPage })),
);
const NotFoundPage = lazy(() =>
  import('./pages/account').then((m) => ({ default: m.NotFoundPage })),
);
const ProfilePage = lazy(() => import('./pages/account').then((m) => ({ default: m.ProfilePage })));
const ProfileSectionPage = lazy(() =>
  import('./pages/account').then((m) => ({ default: m.ProfileSectionPage })),
);
const EventPage = lazy(() => import('./pages/events').then((m) => ({ default: m.EventPage })));
const EventSellersPage = lazy(() =>
  import('./pages/events').then((m) => ({ default: m.EventSellersPage })),
);
const EventSharePage = lazy(() =>
  import('./pages/events').then((m) => ({ default: m.EventSharePage })),
);
const EventSettingsPage = lazy(() =>
  import('./pages/eventSettings').then((m) => ({ default: m.EventSettingsPage })),
);
const EventSettingsSectionPage = lazy(() =>
  import('./pages/eventSettings').then((m) => ({ default: m.EventSettingsSectionPage })),
);
const EventTeamPage = lazy(() =>
  import('./pages/eventTeam').then((m) => ({ default: m.EventTeamPage })),
);
const FeaturesPage = lazy(() =>
  import('./pages/features').then((m) => ({ default: m.FeaturesPage })),
);
const HelperEventsPage = lazy(() =>
  import('./pages/helpers').then((m) => ({ default: m.HelperEventsPage })),
);
const HelperJoinPage = lazy(() =>
  import('./pages/helpers').then((m) => ({ default: m.HelperJoinPage })),
);
const HelpersAdminPage = lazy(() =>
  import('./pages/helpers').then((m) => ({ default: m.HelpersAdminPage })),
);
const HelperShiftsPage = lazy(() =>
  import('./pages/helperShifts').then((m) => ({ default: m.HelperShiftsPage })),
);
const TimeSlotsPage = lazy(() =>
  import('./pages/timeSlots').then((m) => ({ default: m.TimeSlotsPage })),
);
const ShiftCancelPage = lazy(() =>
  import('./pages/shiftPublic').then((m) => ({ default: m.ShiftCancelPage })),
);
const ShiftConfirmPage = lazy(() =>
  import('./pages/shiftPublic').then((m) => ({ default: m.ShiftConfirmPage })),
);
const ShiftMessagesMutePage = lazy(() =>
  import('./pages/shiftPublic').then((m) => ({ default: m.ShiftMessagesMutePage })),
);
const ShiftSignupPage = lazy(() =>
  import('./pages/shiftPublic').then((m) => ({ default: m.ShiftSignupPage })),
);
const MyShiftsPage = lazy(() =>
  import('./pages/shifts').then((m) => ({ default: m.MyShiftsPage })),
);
const InviteOptOutPage = lazy(() =>
  import('./pages/inviteOptOut').then((m) => ({ default: m.InviteOptOutPage })),
);
const OrgBroadcastComposePage = lazy(() =>
  import('./pages/orgMessages').then((m) => ({ default: m.OrgBroadcastComposePage })),
);
const OrgBroadcastPage = lazy(() =>
  import('./pages/orgMessages').then((m) => ({ default: m.OrgBroadcastPage })),
);
const OrgMessagesPage = lazy(() =>
  import('./pages/orgMessages').then((m) => ({ default: m.OrgMessagesPage })),
);
const NewsAdminPage = lazy(() =>
  import('./pages/news').then((m) => ({ default: m.NewsAdminPage })),
);
const NewsFeedPage = lazy(() => import('./pages/news').then((m) => ({ default: m.NewsFeedPage })));
const IntakePage = lazy(() => import('./pages/intake').then((m) => ({ default: m.IntakePage })));
const ItemSearchPage = lazy(() =>
  import('./pages/itemSearch').then((m) => ({ default: m.ItemSearchPage })),
);
const MarketplacePage = lazy(() =>
  import('./pages/marketplace').then((m) => ({ default: m.MarketplacePage })),
);
const OverviewPage = lazy(() =>
  import('./pages/overview').then((m) => ({ default: m.OverviewPage })),
);
const ChangelogPage = lazy(() =>
  import('./pages/changelog').then((m) => ({ default: m.ChangelogPage })),
);
const AppSettingsPage = lazy(() =>
  import('./pages/appSettings').then((m) => ({ default: m.AppSettingsPage })),
);
const AppSettingsSectionPage = lazy(() =>
  import('./pages/appSettings').then((m) => ({ default: m.AppSettingsSectionPage })),
);
const AccessibilityPage = lazy(() =>
  import('./pages/legal').then((m) => ({ default: m.AccessibilityPage })),
);
const DeleteAccountPage = lazy(() =>
  import('./pages/legal').then((m) => ({ default: m.DeleteAccountPage })),
);
const DpaPage = lazy(() => import('./pages/legal').then((m) => ({ default: m.DpaPage }))); // AVV
const ImprintPage = lazy(() => import('./pages/legal').then((m) => ({ default: m.ImprintPage })));
const LegalOverviewPage = lazy(() =>
  import('./pages/legal').then((m) => ({ default: m.LegalOverviewPage })),
);
const PrivacyPage = lazy(() => import('./pages/legal').then((m) => ({ default: m.PrivacyPage }))); // Datenschutz
const PrivacySettingsPage = lazy(() =>
  import('./pages/legal').then((m) => ({ default: m.PrivacySettingsPage })),
);
const SubprocessorsPage = lazy(() =>
  import('./pages/legal').then((m) => ({ default: m.SubprocessorsPage })),
);
const TermsPage = lazy(() => import('./pages/legal').then((m) => ({ default: m.TermsPage })));
const AnalysisPage = lazy(() =>
  import('./pages/analytics/AnalysisPage').then((m) => ({ default: m.AnalysisPage })),
);
const ReportsPage = lazy(() => import('./pages/reports').then((m) => ({ default: m.ReportsPage })));
const PlatformPage = lazy(() =>
  import('./pages/platform').then((m) => ({ default: m.PlatformPage })),
);
const EntryStationPage = lazy(() =>
  import('./pages/entryStation').then((m) => ({ default: m.EntryStationPage })),
);
const EntryPassPage = lazy(() =>
  import('./pages/entryPass').then((m) => ({ default: m.EntryPassPage })),
);
const RunSheetStationPage = lazy(() =>
  import('./pages/runSheet').then((m) => ({ default: m.RunSheetStationPage })),
);
const InspectStationPage = lazy(() =>
  import('./pages/stations').then((m) => ({ default: m.InspectStationPage })),
);
const ReturnStationPage = lazy(() =>
  import('./pages/stations').then((m) => ({ default: m.ReturnStationPage })),
);
const RegistersPage = lazy(() =>
  import('./pages/registers').then((m) => ({ default: m.RegistersPage })),
);
const CreateOrganizationPage = lazy(() =>
  import('./pages/orgs').then((m) => ({ default: m.CreateOrganizationPage })),
);
const OrganizationPage = lazy(() =>
  import('./pages/orgs').then((m) => ({ default: m.OrganizationPage })),
);
const OrgEventsPage = lazy(() =>
  import('./pages/orgs').then((m) => ({ default: m.OrgEventsPage })),
);
const OrgInvitePage = lazy(() =>
  import('./pages/orgs').then((m) => ({ default: m.OrgInvitePage })),
);
const OrgMembersPage = lazy(() =>
  import('./pages/orgs').then((m) => ({ default: m.OrgMembersPage })),
);
const OrgReportsPage = lazy(() =>
  import('./pages/orgs').then((m) => ({ default: m.OrgReportsPage })),
);
const OrgSettingsPage = lazy(() =>
  import('./pages/orgs').then((m) => ({ default: m.OrgSettingsPage })),
);
const PosterPage = lazy(() => import('./pages/poster').then((m) => ({ default: m.PosterPage })));
const MyCarryoverPage = lazy(() =>
  import('./pages/carryover').then((m) => ({ default: m.MyCarryoverPage })),
);
const EventCreatePage = lazy(() =>
  import('./pages/eventCreate').then((m) => ({ default: m.EventCreatePage })),
);
const EventTemplatesPage = lazy(() =>
  import('./pages/eventTemplates').then((m) => ({ default: m.EventTemplatesPage })),
);
const EventRepeatPage = lazy(() =>
  import('./pages/seasonChange').then((m) => ({ default: m.EventRepeatPage })),
);
const InvitePreviousSellersPage = lazy(() =>
  import('./pages/seasonChange').then((m) => ({ default: m.InvitePreviousSellersPage })),
);
const CashCountPage = lazy(() =>
  import('./pages/cash').then((m) => ({ default: m.CashCountPage })),
);
const LivePage = lazy(() => import('./pages/live').then((m) => ({ default: m.LivePage })));
const SettlementPage = lazy(() =>
  import('./pages/settlement').then((m) => ({ default: m.SettlementPage })),
);
const HelperListPrintPage = lazy(() =>
  import('./pages/listPrint').then((m) => ({ default: m.HelperListPrintPage })),
);
const PayoutListPrintPage = lazy(() =>
  import('./pages/listPrint').then((m) => ({ default: m.PayoutListPrintPage })),
);
const ReturnListPrintPage = lazy(() =>
  import('./pages/listPrint').then((m) => ({ default: m.ReturnListPrintPage })),
);
const PickupListPrintPage = lazy(() =>
  import('./pages/listPrint').then((m) => ({ default: m.PickupListPrintPage })),
);
const SellerListPrintPage = lazy(() =>
  import('./pages/listPrint').then((m) => ({ default: m.SellerListPrintPage })),
);
const ShiftListPrintPage = lazy(() =>
  import('./pages/listPrint').then((m) => ({ default: m.ShiftListPrintPage })),
);
const OwnSettlementPrintPage = lazy(() =>
  import('./pages/settlementPrint').then((m) => ({ default: m.OwnSettlementPrintPage })),
);
const SettlementPrintPage = lazy(() =>
  import('./pages/settlementPrint').then((m) => ({ default: m.SettlementPrintPage })),
);
const PayoutReceiptPrintPage = lazy(() =>
  import('./pages/settlementPrint').then((m) => ({ default: m.PayoutReceiptPrintPage })),
);
const DefectNoticePrintPage = lazy(() =>
  import('./pages/listPrint').then((m) => ({ default: m.DefectNoticePrintPage })),
);
const PickupSlipPrintPage = lazy(() =>
  import('./pages/listPrint').then((m) => ({ default: m.PickupSlipPrintPage })),
);
const SalesListPrintPage = lazy(() =>
  import('./pages/listPrint').then((m) => ({ default: m.SalesListPrintPage })),
);
const TermsSheetPrintPage = lazy(() =>
  import('./pages/listPrint').then((m) => ({ default: m.TermsSheetPrintPage })),
);
const EventReportPrintPage = lazy(() =>
  import('./pages/reportPrint').then((m) => ({ default: m.EventReportPrintPage })),
);
const OrgReportPrintPage = lazy(() =>
  import('./pages/reportPrint').then((m) => ({ default: m.OrgReportPrintPage })),
);
const RegisterPrintPage = lazy(() =>
  import('./pages/reportPrint').then((m) => ({ default: m.RegisterPrintPage })),
);
const EventDocumentsPage = lazy(() =>
  import('./pages/documents').then((m) => ({ default: m.EventDocumentsPage })),
);
const OrgDocumentsPage = lazy(() =>
  import('./pages/documents').then((m) => ({ default: m.OrgDocumentsPage })),
);
const MyItemImportPage = lazy(() =>
  import('./pages/sellers').then((m) => ({ default: m.MyItemImportPage })),
);
const MyItemsPage = lazy(() => import('./pages/sellers').then((m) => ({ default: m.MyItemsPage })));
const MyLabelsPage = lazy(() =>
  import('./pages/sellers').then((m) => ({ default: m.MyLabelsPage })),
);
const MyRegistrationPage = lazy(() =>
  import('./pages/sellers').then((m) => ({ default: m.MyRegistrationPage })),
);
const MyRegistrationsPage = lazy(() =>
  import('./pages/sellers').then((m) => ({ default: m.MyRegistrationsPage })),
);
const MyReportsPage = lazy(() =>
  import('./pages/sellers').then((m) => ({ default: m.MyReportsPage })),
);
const MySalesStatusPage = lazy(() =>
  import('./pages/sellers').then((m) => ({ default: m.MySalesStatusPage })),
);
const PublicEventPage = lazy(() =>
  import('./pages/sellers').then((m) => ({ default: m.PublicEventPage })),
);
const BulkCapturePage = lazy(() =>
  import('./pages/bulkCapture').then((m) => ({ default: m.BulkCapturePage })),
);
const HelpIndexPage = lazy(() =>
  import('./pages/help').then((m) => ({ default: m.HelpIndexPage })),
);
const HelpArticlePage = lazy(() =>
  import('./pages/help').then((m) => ({ default: m.HelpArticlePage })),
);

/** Ladeanzeige beim Öffnen einer Seite. Angesagt für Hilfstechnik, ohne Bewegung. */
function PageLoading() {
  return (
    <p className="muted" role="status">
      Wird geladen …
    </p>
  );
}

/**
 * Grenze für die nachgeladenen Seiten. Sie liegt unterhalb von Layout und (bei Basar-Seiten) unterhalb der Basar-Navigation,
 * damit Menü und Navigation beim Nachladen stehen bleiben.
 */
function PageBoundary() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Outlet />
    </Suspense>
  );
}

/** Adressen sind deutsch, weil die Links in den E-Mails des Servers genau diese Pfade benutzen. */
export function App() {
  useNativeLinks();
  // Wartende Kaufbelege tragen Adressen: Zu alte werden beim Start gelöscht, auch ohne geöffnete Kassenseite
  useEffect(() => {
    void purgeStaleInvoices(Date.now(), INVOICE_MAX_AGE_MS).catch(() => undefined);
  }, []);
  return (
    <Routes>
      <Route element={<Layout />}>
        {/* Sofort geladen: Anmelden, Registrieren, Kasse und Start */}
        <Route path="anmelden" element={<LoginPage />} />
        <Route path="registrieren" element={<RegisterPage />} />
        <Route path="kasse-koppeln" element={<KeyedPairingPage />} />
        <Route path="kasse-geraet/:eventId" element={<DevicePosPage />} />
        <Route path="kasse-scannen" element={<PairScanPage />} />
        <Route index element={<RootPage />} />

        <Route element={<PageBoundary />}>
          <Route path="impressum" element={<ImprintPage />} />
          <Route path="nutzungsbedingungen" element={<TermsPage />} />
          <Route path="datenschutz" element={<PrivacyPage />} />
          <Route path="neuerungen" element={<ChangelogPage />} />
          <Route path="datenschutz-einstellungen" element={<PrivacySettingsPage />} />
          <Route path="rechtliches" element={<LegalOverviewPage />} />
          <Route path="avv" element={<DpaPage />} />
          <Route path="unterauftragsverarbeiter" element={<SubprocessorsPage />} />
          <Route path="barrierefreiheit" element={<AccessibilityPage />} />
          <Route path="konto-loeschen" element={<DeleteAccountPage />} />
          <Route path="email-bestaetigen" element={<VerifyEmailPage />} />
          <Route path="passwort-vergessen" element={<ForgotPasswordPage />} />
          <Route path="passwort-zuruecksetzen" element={<ResetPasswordPage />} />
          <Route path="einladung" element={<AcceptInvitationPage />} />
          <Route path="helfen" element={<HelperJoinPage />} />
          <Route path="schicht-eintragen" element={<ShiftSignupPage />} />
          <Route path="schicht-abmelden" element={<ShiftCancelPage />} />
          <Route path="schicht-nachrichten" element={<ShiftMessagesMutePage />} />
          <Route path="abmelden" element={<InviteOptOutPage />} />
          <Route path="schicht-bestaetigen" element={<ShiftConfirmPage />} />
          <Route path="b/:orgSlug/:eventSlug" element={<PublicEventPage />} />
          <Route path="basare-entdecken" element={<MarketplacePage />} />
          <Route path="funktionen" element={<FeaturesPage />} />
          <Route path="hilfe" element={<HelpIndexPage />} />
          <Route path="hilfe/:slug" element={<HelpArticlePage />} />

          <Route element={<RequireAuth />}>
            <Route path="basar-anbieten" element={<CreateOrganizationPage />} />
            <Route path="organisationen/:orgId" element={<OrganizationPage />} />
            <Route path="organisationen/:orgId/basare" element={<OrgEventsPage />} />
            <Route path="organisationen/:orgId/basar-anlegen" element={<EventCreatePage />} />
            <Route path="organisationen/:orgId/basar-vorlagen" element={<EventTemplatesPage />} />
            <Route path="organisationen/:orgId/mitglieder" element={<OrgMembersPage />} />
            <Route path="organisationen/:orgId/einladen" element={<OrgInvitePage />} />
            <Route path="organisationen/:orgId/berichte" element={<OrgReportsPage />} />
            <Route
              path="organisationen/:orgId/berichte/:report/druck"
              element={<OrgReportPrintPage />}
            />
            <Route path="organisationen/:orgId/dokumente" element={<OrgDocumentsPage />} />
            <Route path="organisationen/:orgId/nachrichten" element={<OrgMessagesPage />} />
            <Route
              path="organisationen/:orgId/nachrichten/neu"
              element={<OrgBroadcastComposePage />}
            />
            <Route
              path="organisationen/:orgId/nachrichten/:broadcastId"
              element={<OrgBroadcastPage />}
            />
            <Route path="organisationen/:orgId/einstellungen" element={<OrgSettingsPage />} />
            <Route path="kasse" element={<QuickRegisterPage />} />
            <Route path="einstellungen" element={<AppSettingsPage />} />
            <Route path="einstellungen/:section" element={<AppSettingsSectionPage />} />
            <Route path="profil" element={<ProfilePage />} />
            <Route path="profil/:section" element={<ProfileSectionPage />} />
            <Route path="basare/:eventId" element={<EventShell />}>
              <Route element={<PageBoundary />}>
                <Route index element={<EventPage />} />
                <Route path="einstellungen" element={<EventSettingsPage />} />
                <Route path="einstellungen/:section" element={<EventSettingsSectionPage />} />
                <Route path="teilen" element={<EventSharePage />} />
                <Route path="verkaeufer" element={<EventSellersPage />} />
                <Route path="verkaeufer/druck" element={<SellerListPrintPage />} />
                <Route path="verkaeufer/verkaufsliste/druck" element={<SalesListPrintPage />} />
                <Route path="team" element={<EventTeamPage />} />
                <Route path="plakat" element={<PosterPage />} />
                <Route path="wiederholen" element={<EventRepeatPage />} />
                <Route path="fruehere-verkaeufer" element={<InvitePreviousSellersPage />} />
                <Route path="annahme" element={<IntakePage />} />
                <Route path="annahme/rueckgabeliste/druck" element={<ReturnListPrintPage />} />
                <Route path="annahme/abholliste/druck" element={<PickupListPrintPage />} />
                <Route path="annahme/abholscheine/druck" element={<PickupSlipPrintPage />} />
                <Route path="annahme/maengelzettel/druck" element={<DefectNoticePrintPage />} />
                <Route path="annahme/bedingungen/druck" element={<TermsSheetPrintPage />} />
                <Route path="artikel" element={<ItemSearchPage />} />
                <Route path="kasse" element={<PosPage />} />
                <Route path="kassenplaetze" element={<RegistersPage />} />
                <Route path="uebersicht" element={<OverviewPage />} />
                <Route path="berichte" element={<ReportsPage />} />
                <Route path="berichte/:slug" element={<AnalysisPage />} />
                <Route path="dokumente" element={<EventDocumentsPage />} />
                <Route path="berichte/:report/druck" element={<EventReportPrintPage />} />
                <Route path="helfer" element={<HelpersAdminPage />} />
                <Route path="helfer/liste/druck" element={<HelperListPrintPage />} />
                <Route path="helferschichten" element={<HelperShiftsPage />} />
                <Route path="helferschichten/liste/druck" element={<ShiftListPrintPage />} />
                <Route path="zeitfenster" element={<TimeSlotsPage />} />
                <Route path="rueckgabe" element={<ReturnStationPage />} />
                <Route path="pruefen" element={<InspectStationPage />} />
                <Route path="laufzettel" element={<RunSheetStationPage />} />
                <Route path="einlass" element={<EntryStationPage />} />
                <Route path="nachrichten" element={<TeamInboxPage />}>
                  <Route path="neu" element={<ComposeSheet fixed />} />
                  <Route path=":threadId" element={<TeamThreadPage />} />
                </Route>
                <Route path="neuigkeiten" element={<NewsAdminPage />} />
                <Route path="abrechnung" element={<SettlementPage />} />
                <Route path="abrechnung/druck" element={<SettlementPrintPage />} />
                <Route path="abrechnung/auszahlung/druck" element={<PayoutListPrintPage />} />
                <Route path="abrechnung/quittung/druck" element={<PayoutReceiptPrintPage />} />
                <Route path="bargeld" element={<CashCountPage />} />
                <Route path="bargeld/:registerId/druck" element={<RegisterPrintPage />} />
                <Route path="live" element={<LivePage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
            <Route path="einsaetze" element={<HelperEventsPage />} />
            <Route path="einlass-pass" element={<EntryPassPage />} />
            <Route path="meine-schichten" element={<MyShiftsPage />} />
            <Route path="neuigkeiten" element={<NewsFeedPage />} />
            <Route path="nachrichten" element={<InboxPage />}>
              <Route path="neu" element={<ComposeSheet />} />
              <Route path="org/:broadcastId" element={<OrgMessagePage />} />
              <Route path="basar/:eventId/:threadId" element={<TeamThreadPage fromMailbox />} />
              <Route path=":threadId" element={<ThreadPage />} />
            </Route>
            <Route path="plattform" element={<PlatformPage />} />
            <Route path="plattform/:section" element={<PlatformPage />} />
            <Route path="meine-anmeldungen" element={<MyRegistrationsPage />} />
            <Route path="meine-anmeldungen/:registrationId" element={<MyRegistrationPage />} />
            <Route path="meine-anmeldungen/:registrationId/artikel" element={<MyItemsPage />} />
            <Route
              path="meine-anmeldungen/:registrationId/artikel/neu"
              element={<MyItemsPage view="new" />}
            />
            <Route path="meine-anmeldungen/:registrationId/import" element={<MyItemImportPage />} />
            <Route path="meine-anmeldungen/:registrationId/lager" element={<MyCarryoverPage />} />
            <Route
              path="meine-anmeldungen/:registrationId/schnellerfassung"
              element={<BulkCapturePage />}
            />
            <Route
              path="meine-anmeldungen/:registrationId/verkauf"
              element={<MySalesStatusPage />}
            />
            <Route path="meine-anmeldungen/:registrationId/etiketten" element={<MyLabelsPage />} />
            <Route path="meine-anmeldungen/:registrationId/berichte" element={<MyReportsPage />} />
            <Route
              path="meine-anmeldungen/:registrationId/abrechnung/druck"
              element={<OwnSettlementPrintPage />}
            />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
